/**
 * Regional Data Service Implementation
 *
 * Provides access to regional demographic data, ministry opportunities,
 * and community context reports from MPO/COG data portals.
 *
 * Premium feature - requires 'regional_analytics' module access.
 */

import { BaseService } from './base/BaseService';

/**
 * Regional data availability check response
 */
export interface RegionalAvailability {
  available: boolean;
  reason: 'region_matched' | 'region_not_supported' | 'premium_required' | 'no_location' | 'missing_coordinates' | 'error';
  message?: string;
  region?: RegionInfo;
  church_location?: ChurchLocationInfo;
  supported_regions?: SupportedRegion[];
  nearest_regions?: NearestRegion[];
  upgrade_url?: string;
  request_region_url?: string;
}

/**
 * Region information
 */
export interface RegionInfo {
  id: string;
  name: string;
  full_name: string;
  coverage_description?: string;
  equity_framework_name?: string;
  data_source_name?: string;
  data_vintage?: string;
  portal_url?: string;
}

/**
 * Church location information
 */
export interface ChurchLocationInfo {
  city?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
  zip_code?: string;
}

/**
 * Supported region summary
 */
export interface SupportedRegion {
  id: string;
  name: string;
  full_name: string;
  coverage_description: string;
  states_covered: string;
  equity_framework_name: string;
  portal_url: string;
  verification_status: string;
  bounding_box?: {
    min_lat: number;
    max_lat: number;
    min_lng: number;
    max_lng: number;
  };
}

/**
 * Nearest region with distance
 */
export interface NearestRegion extends Omit<SupportedRegion, 'bounding_box'> {
  approximate_distance_miles: number;
}

/**
 * Full regional context data
 */
export interface RegionalContext {
  available: boolean;
  reason?: string;
  message?: string;
  region?: RegionInfo;
  location?: {
    city?: string;
    state?: string;
    block_group_id?: string;
  };
  demographics?: DemographicsData;
  equity?: EquityData;
  ministry_opportunities?: MinistryOpportunities;
  data_freshness?: DataFreshness;
  narrative_summary?: string;
  upgrade_url?: string;
}

/**
 * Demographics data
 */
export interface DemographicsData {
  total_population: number;
  population_under_18: number;
  population_65_plus: number;
  median_income: number;
  race_ethnicity?: {
    white: number;
    hispanic: number;
    black: number;
    asian: number;
    other: number;
  };
  housing_tenure?: {
    owner_occupied: number;
    renter_occupied: number;
    owner_percentage: number;
    renter_percentage: number;
  };
}

/**
 * Equity/Environmental Justice data
 */
export interface EquityData {
  is_equity_area: boolean;
  framework_name: string;
  framework_type: string;
  framework_description: string;
  indicators?: Record<string, boolean | number | string>;
}

/**
 * Ministry opportunity scores and recommendations
 */
export interface MinistryOpportunities {
  youth_ministry_score: number;
  senior_ministry_score: number;
  economic_assistance_score: number;
  diversity_index: number;
  underserved_area: boolean;
  key_opportunities: string[];
  detailed_scores?: {
    youth?: {
      population: number;
      percentage: number;
      score: number;
    };
    senior?: {
      population: number;
      percentage: number;
      score: number;
    };
    economic?: {
      median_income: number;
      benchmark_income: number;
      score: number;
    };
  };
}

/**
 * Data freshness information
 */
export interface DataFreshness {
  source: string;
  vintage?: string;
  fetched_at: string;
  expires_at: string;
}

/**
 * Enhanced Community Insights (Tier 2) - Heat Map Feature Properties
 */
export interface HeatMapFeatureProperties {
  geoid: string;
  population: number;
  median_income: number | null;
  youth_percentage: number;
  senior_percentage: number;
  renter_percentage: number;
  is_equity_area: boolean;
  need_score: number;
  need_factors: string[];
  direction_from_church: string;
  distance_from_church_miles: number;
  location_description: string;
  heat_color: string;
}

/**
 * GeoJSON Feature for Heat Map
 */
export interface HeatMapFeature {
  type: 'Feature';
  geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: number[][][] | number[][][][];
  };
  properties: HeatMapFeatureProperties;
}

/**
 * GeoJSON FeatureCollection for Heat Map
 */
export interface HeatMapGeoJSON {
  type: 'FeatureCollection';
  features: HeatMapFeature[];
  metadata: {
    center: { lat: number; lng: number };
    block_group_count: number;
    legend: {
      high_need: { min: number; color: string; label: string };
      moderate_need: { min: number; color: string; label: string };
      lower_need: { min: number; color: string; label: string };
    };
  };
}

/**
 * Direction Analysis - Church-relative positioning
 */
export interface DirectionAnalysis {
  primary_direction: string | null;
  primary_stats?: {
    block_groups: number;
    population: number;
    avg_need_score: number;
  };
  description: string;
  secondary_direction?: string;
  strategic_insight?: string;
  all_directions: Array<{
    direction: string;
    block_groups: number;
    population: number;
    avg_need_score: number;
    recommendation: string;
  }>;
}

/**
 * Priority Area with church-relative location
 */
export interface PriorityArea {
  geoid: string;
  need_score: number;
  population: number;
  need_factors: string[];
  direction_from_church: string;
  distance_miles: number;
  location_description: string;
}

/**
 * Heat Map Annotations for display
 */
export interface HeatMapAnnotations {
  title: string;
  description: string;
  data_source: string;
  methodology: string;
  interpretation_guide: Array<{
    color: string;
    label: string;
    description: string;
  }>;
}

/**
 * Heat Map Statistics
 */
export interface HeatMapStatistics {
  total_block_groups: number;
  total_population: number;
  average_need_score: number;
  highest_need_score: number;
  lowest_need_score: number;
  median_income_range: {
    min: number;
    max: number;
    average: number;
  };
  equity_areas: {
    count: number;
    percentage: number;
    description: string;
  };
  high_need_areas_count: number;
  high_need_population: number;
}

/**
 * Enhanced Heat Map Data
 */
export interface EnhancedHeatMap {
  available: boolean;
  map_title?: string;
  map_subtitle?: string;
  map_center?: {
    latitude: number | null;
    longitude: number | null;
    label: string;
  };
  geojson?: HeatMapGeoJSON;
  annotations?: HeatMapAnnotations;
  statistics?: HeatMapStatistics;
  priority_areas?: {
    description: string;
    count: number;
    areas: PriorityArea[];
    by_direction: Record<string, { count: number; population: number; avg_need_score: number }>;
  };
  direction_analysis?: DirectionAnalysis;
  ministry_recommendations?: {
    based_on_map: Array<{
      priority: string;
      category: string;
      recommendation: string;
      action: string;
    }>;
    geographic_strategy: string;
  };
}

/**
 * Enhanced Ministry Opportunities (Tier 2)
 */
export interface EnhancedMinistryOpportunities {
  community_profile: {
    total_population: number;
    median_income: number;
    youth_population: number;
    senior_population: number;
    renter_percentage: number;
    diversity_index: number;
    is_equity_area: boolean;
    block_groups_analyzed: number;
    community_type: string;
  };
  priority_ministries: Array<{
    type: string;
    priority: 'critical' | 'high' | 'moderate';
    score: number;
    insight: string;
  }>;
  detailed_scores: {
    youth?: {
      population: number;
      percentage: number;
      score: number;
      programs_suggested: string[];
    };
    senior?: {
      population: number;
      percentage: number;
      score: number;
      programs_suggested: string[];
    };
    economic?: {
      median_income: number;
      benchmark_income: number;
      income_ratio: number;
      score: number;
      programs_suggested: string[];
    };
    housing?: {
      renter_percentage: number;
      owner_percentage: number;
      score: number;
      programs_suggested: string[];
    };
    diversity?: {
      diversity_index: number;
      top_groups: Array<Record<string, number>>;
      score: number;
      programs_suggested: string[];
    };
    equity?: {
      is_equity_area: boolean;
      equity_percentage: number;
      framework: string;
    };
  };
  key_opportunities: string[];
  aggregation_info: {
    block_groups: number;
    search_radius_miles: number;
  };
}

/**
 * Partnership Opportunity - A potential ministry partner
 */
export interface PartnershipOpportunity {
  name: string;
  latitude: number;
  longitude: number;
  distance_miles: number;
  address?: string | null;
  phone?: string | null;
  website?: string | null;
  email?: string | null;
  description?: string | null;
  opening_hours?: string | null;
  // Category-specific fields
  school_type?: string | null;
  operator?: string | null;
  denomination?: string | null;
  religion?: string | null;
  healthcare_type?: string | null;
  specialty?: string | null;
}

/**
 * Partnership Category with partners
 */
export interface PartnershipCategory {
  name: string;
  icon: string;
  description: string;
  ministry_ideas: string[];
  partners: PartnershipOpportunity[];
  count: number;
  radius_miles: number;
  error?: string;
}

/**
 * Partnership Insight - Ministry recommendations based on data
 */
export interface PartnershipInsight {
  priority: 'high' | 'medium' | 'low';
  category: string;
  insight: string;
  action: string;
}

/**
 * Partnership Opportunities Response
 */
export interface PartnershipOpportunitiesResponse {
  available: boolean;
  church_location?: {
    latitude: number;
    longitude: number;
  };
  church?: {
    name: string;
    location: {
      address: string;
      city: string;
      state: string;
      latitude: number;
      longitude: number;
    };
  };
  categories: Record<string, PartnershipCategory>;
  total_partners: number;
  summary: Record<string, number>;
  insights: PartnershipInsight[];
  reason?: string;
  message?: string;
}

/**
 * Enhanced Community Insights Response (Tier 2)
 */
export interface EnhancedCommunityInsights {
  available: boolean;
  tier: 'enhanced';
  subscription_level: 2;

  // Location & Coverage
  location: {
    city?: string;
    state?: string;
    latitude?: number;
    longitude?: number;
    block_group_id?: string;
    block_group_count?: number;
    search_radius_miles?: number;
  };
  region: RegionInfo;

  // Aggregated Demographics
  demographics: {
    total_population?: number;
    median_income?: number;
    population_under_18?: number;
    population_65_plus?: number;
    housing_tenure?: {
      owner_percentage: number;
      renter_percentage: number;
    };
    race_ethnicity?: Record<string, number>;
    block_groups_analyzed?: number;
    search_radius_miles?: number;
  };

  // Equity Analysis
  equity: {
    is_equity_area?: boolean;
    equity_area_percentage?: number;
    framework_name?: string;
    framework_description?: string;
  };

  // Enhanced Ministry Opportunities
  ministry_opportunities: EnhancedMinistryOpportunities;

  // Heat Map Visualization Data
  heat_map: EnhancedHeatMap;

  // Data Freshness
  data_freshness: DataFreshness;

  // Error handling
  reason?: string;
  message?: string;
}

/**
 * Regional Data Service
 */
export class RegionalDataService extends BaseService {
  private regionalUrl: string;

  constructor(apiUrl: string) {
    super('RegionalDataService');
    this.regionalUrl = '/analytics/regional';
  }

  /**
   * Check if regional data is available for the current church
   */
  async checkAvailability(): Promise<RegionalAvailability> {
    try {
      const response = await this.get<RegionalAvailability>(
        `${this.regionalUrl}/check/`
      );
      return response;
    } catch (error: any) {
      // Handle 403 - premium required
      if (error.response?.status === 403) {
        return error.response.data as RegionalAvailability;
      }
      console.error('[RegionalDataService] Failed to check availability:', error);
      return {
        available: false,
        reason: 'error',
        message: 'Unable to check regional data availability'
      };
    }
  }

  /**
   * Get full regional context data including demographics and opportunities
   */
  async getRegionalContext(): Promise<RegionalContext> {
    try {
      const response = await this.get<RegionalContext>(
        `${this.regionalUrl}/context/`
      );
      return response;
    } catch (error: any) {
      // Handle 403 - premium required
      if (error.response?.status === 403) {
        return error.response.data as RegionalContext;
      }
      console.error('[RegionalDataService] Failed to fetch regional context:', error);
      return {
        available: false,
        reason: 'error',
        message: 'Unable to fetch regional data'
      };
    }
  }

  /**
   * Get ministry opportunity scores only (lighter weight)
   */
  async getMinistryOpportunities(): Promise<{
    available: boolean;
    region?: RegionInfo;
    ministry_opportunities?: MinistryOpportunities;
    data_freshness?: DataFreshness;
  }> {
    try {
      const response = await this.get<any>(
        `${this.regionalUrl}/opportunities/`
      );
      return response;
    } catch (error: any) {
      if (error.response?.status === 403) {
        return { available: false, ...error.response.data };
      }
      console.error('[RegionalDataService] Failed to fetch opportunities:', error);
      return { available: false };
    }
  }

  /**
   * Get community profile with narrative summary
   */
  async getCommunityProfile(): Promise<RegionalContext> {
    try {
      const response = await this.get<RegionalContext>(
        `${this.regionalUrl}/profile/`
      );
      return response;
    } catch (error: any) {
      if (error.response?.status === 403) {
        return error.response.data as RegionalContext;
      }
      console.error('[RegionalDataService] Failed to fetch community profile:', error);
      return {
        available: false,
        reason: 'error',
        message: 'Unable to fetch community profile'
      };
    }
  }

  /**
   * Get list of all supported regions
   * This endpoint does NOT require premium access
   */
  async getSupportedRegions(): Promise<{
    count: number;
    regions: SupportedRegion[];
  }> {
    try {
      const response = await this.get<{ count: number; regions: SupportedRegion[] }>(
        `${this.regionalUrl}/supported-regions/`
      );
      return response;
    } catch (error: any) {
      console.error('[RegionalDataService] Failed to fetch supported regions:', error);
      return { count: 0, regions: [] };
    }
  }

  /**
   * Get nearest supported regions to church location
   * This endpoint does NOT require premium access
   */
  async getNearestRegions(): Promise<{
    church_location?: ChurchLocationInfo;
    nearest_regions: NearestRegion[];
  }> {
    try {
      const response = await this.get<{
        church_location?: ChurchLocationInfo;
        nearest_regions: NearestRegion[];
      }>(`${this.regionalUrl}/nearest/`);
      return response;
    } catch (error: any) {
      console.error('[RegionalDataService] Failed to fetch nearest regions:', error);
      return { nearest_regions: [] };
    }
  }

  /**
   * Get Enhanced Community Insights (Tier 2 Premium Feature)
   *
   * Returns comprehensive community analysis including:
   * - Aggregated block group demographics (40+ block groups)
   * - Heat map GeoJSON data with need scores
   * - Directional analysis (North/South/East/West of church)
   * - Priority ministry areas with church-relative locations
   * - Detailed ministry opportunity scoring with program suggestions
   * - Geographic strategy recommendations
   */
  async getEnhancedCommunityInsights(): Promise<EnhancedCommunityInsights> {
    try {
      const response = await this.get<EnhancedCommunityInsights>(
        `${this.regionalUrl}/enhanced-insights/`
      );
      return response;
    } catch (error: any) {
      // Handle 403 - premium required
      if (error.response?.status === 403) {
        return {
          available: false,
          tier: 'enhanced',
          subscription_level: 2,
          reason: 'premium_required',
          message: 'Enhanced Community Insights requires a Tier 2 subscription',
          ...error.response.data
        } as EnhancedCommunityInsights;
      }
      // Handle 404 - not in supported region
      if (error.response?.status === 404) {
        return {
          available: false,
          tier: 'enhanced',
          subscription_level: 2,
          reason: error.response.data?.reason || 'no_data',
          message: error.response.data?.message || 'Enhanced data not available for your region',
          ...error.response.data
        } as EnhancedCommunityInsights;
      }
      console.error('[RegionalDataService] Failed to fetch enhanced insights:', error);
      return {
        available: false,
        tier: 'enhanced',
        subscription_level: 2,
        reason: 'error',
        message: 'Unable to fetch enhanced community insights'
      } as EnhancedCommunityInsights;
    }
  }

  /**
   * Get Partnership Opportunities near the church
   *
   * Finds potential ministry partners using free OpenStreetMap data:
   * - Schools (tutoring, mentoring, after-school programs)
   * - Other churches (collaborative outreach, shared resources)
   * - Community centers (program partnerships, space sharing)
   * - Libraries (literacy programs, education partnerships)
   * - Healthcare facilities (health ministry referrals)
   * - Social services (community support partnerships)
   * - Food resources (food pantry partnerships)
   * - Senior facilities (visitation, wellness programs)
   *
   * @param categories Optional array of categories to filter by
   */
  async getPartnershipOpportunities(
    categories?: string[]
  ): Promise<PartnershipOpportunitiesResponse> {
    try {
      const params = categories ? `?categories=${categories.join(',')}` : '';
      const response = await this.get<PartnershipOpportunitiesResponse>(
        `${this.regionalUrl}/partnerships/${params}`
      );
      return response;
    } catch (error: any) {
      // Handle 403 - premium required
      if (error.response?.status === 403) {
        return {
          available: false,
          categories: {},
          total_partners: 0,
          summary: {},
          insights: [],
          reason: 'premium_required',
          message: 'Partnership opportunities requires a premium subscription',
          ...error.response.data
        } as PartnershipOpportunitiesResponse;
      }
      console.error('[RegionalDataService] Failed to fetch partnerships:', error);
      return {
        available: false,
        categories: {},
        total_partners: 0,
        summary: {},
        insights: [],
        reason: 'error',
        message: 'Unable to fetch partnership opportunities'
      };
    }
  }

  /**
   * Force refresh of cached regional data
   */
  async refreshData(): Promise<RegionalContext> {
    try {
      const response = await this.post<{
        success: boolean;
        message: string;
        data: RegionalContext;
      }>(`${this.regionalUrl}/refresh/`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 403) {
        return error.response.data as RegionalContext;
      }
      console.error('[RegionalDataService] Failed to refresh data:', error);
      return {
        available: false,
        reason: 'error',
        message: 'Unable to refresh regional data'
      };
    }
  }

  /**
   * Clear local cache
   */
  clearLocalCache(): void {
    this.clearCache();
  }
}

// Export singleton factory
let regionalDataServiceInstance: RegionalDataService | null = null;

export function getRegionalDataService(apiUrl?: string): RegionalDataService {
  if (!regionalDataServiceInstance) {
    regionalDataServiceInstance = new RegionalDataService(apiUrl || '');
  }
  return regionalDataServiceInstance;
}
